import type { Maybe } from '@adornis/base/utilTypes.js';
import { alertDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import { GroupsAPI } from '../../groups/api/GroupsAPI.js';
import { Group } from '../../groups/entities/Group.js';
import { School } from '../../groups/entities/School.js';
import { LegacyDebitorNumber } from '../entities/LegacyDebitorNumber.js';

const ChangeDebitorennummerDialog = stateful(
  (props: {
    debitorennummer: Maybe<string>;
    legacyDebitorNumbers: Maybe<LegacyDebitorNumber[]>;
    resolve: (value: { debitorennummer: string; legacyDebitorNumbers: LegacyDebitorNumber[] }) => void;
    reject: () => void;
  }) => {
    const [debitorennummer, setDebitorennummer] = useState(props.debitorennummer);
    const [legacyDebitorNumbers, setLegacyDebitorNumbers] = useState(props.legacyDebitorNumbers);

    return html`
      <div class="dialog-container">
        ${DialogHeader({
          title: 'Debitorennummern ändern',
          close: props.reject,
          closeLabel: 'Dialog schließen',
        })}
        <p>
          Hier können Sie die Debitorennummern für den Benutzer verwalten. Die Hauptdebitorennummer ist die primäre
          Nummer im System und muss einzigartig sein. Die alten Debitorennummern sind Nummern aus früheren Systemen und
          werden benützt um eventuelle alte externe Daten zuzuordnen.
        </p>
        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold text-base-content/70">Debitorennummer</span>
          ${TextInput({ value: debitorennummer, onPicked: value => setDebitorennummer(value) })}
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-semibold text-base-content/70">Alte Debitorennummern</span>

          ${repeat(
            legacyDebitorNumbers ?? [],
            debitorennummer => debitorennummer,
            (debitorennummer, index) => html`
              <div class="card card-body items-center flex-row! min-w-1xl">
                ${EntityPickerInput({
                  placeholder: 'Schule',
                  placeholderMode: 'hide',
                  value: debitorennummer.scope,
                  class: Group,
                  operation: GroupsAPI.subscribeGroups,
                  filter: new AdornisFilter({
                    searchFields: ['name'],
                    aspects: [
                      new BooleanAspect({ customID: 'onlyInternal', value: true }),
                      new StringAspect({ fieldName: '_class', value: School._class }),
                    ],
                  }),

                  selectionSet: { name: 1, _id: 1 },
                  onPicked: value => {
                    const legacyDebitorNumbersCopy = [...(legacyDebitorNumbers ?? [])];
                    const entry = legacyDebitorNumbersCopy.find((_, i) => i === index);
                    if (!entry) return;
                    entry.scope = value ?? '';
                    setLegacyDebitorNumbers(legacyDebitorNumbersCopy);
                  },
                })}
                ${TextInput({
                  value: debitorennummer.debitorNumber,
                  placeholder: 'Alte Debitorennummer',
                  placeholderMode: 'hide',
                  onPicked: value => {
                    const legacyDebitorNumbersCopy = [...(legacyDebitorNumbers ?? [])];
                    const entry = legacyDebitorNumbersCopy.find((_, i) => i === index);
                    if (!entry) return;
                    entry.debitorNumber = value ?? '';
                    setLegacyDebitorNumbers(legacyDebitorNumbersCopy);
                  },
                })}
                <button
                  class="btn btn-ghost btn-sm btn-error"
                  @click=${() => {
                    const newLegacyDebitorNumbers = [...(legacyDebitorNumbers ?? [])];
                    newLegacyDebitorNumbers.splice(index, 1);
                    setLegacyDebitorNumbers(newLegacyDebitorNumbers);
                  }}
                >
                  <span class="icon-[material-symbols--delete]"></span>
                </button>
              </div>
            `,
          )}
          <button
            class="btn btn-dash btn-block justify-start"
            @click=${() => {
              const newLegacyDebitorNumbers = [...(legacyDebitorNumbers ?? []), new LegacyDebitorNumber({})];
              setLegacyDebitorNumbers(newLegacyDebitorNumbers);
            }}
          >
            <span class="icon-[material-symbols--add]"></span>
            Alte Debitorennummer hinzufügen
          </button>
        </div>

        <button
          class="btn btn-primary"
          @click=${() => {
            if (!debitorennummer) {
              alertDialog('Debitorennummer darf nicht leer sein!');
              return;
            }
            props.resolve({
              debitorennummer: debitorennummer ?? '',
              // filter out non full values to only keep entries with debitorNumber
              legacyDebitorNumbers: (legacyDebitorNumbers ?? []).filter(o => o.debitorNumber),
            });
          }}
        >
          Annehmen
        </button>
      </div>
    `;
  },
);

export async function openChangeDebitorennummerDialog(
  debitorennummer: Maybe<string>,
  legacyDebitorNumbers: Maybe<LegacyDebitorNumber[]>,
) {
  return showDialog<{ debitorennummer: string; legacyDebitorNumbers: LegacyDebitorNumber[] }>({
    content: (resolve, reject) =>
      ChangeDebitorennummerDialog({ debitorennummer, legacyDebitorNumbers, resolve, reject }),
    modalBoxClasses: 'max-w-4xl',
  });
}
